import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import MenuItemDetailsModal from 'catering/components/Menu/MenuItemDetailsModal';
import MenuItemModalFooter from 'catering/components/Menu/MenuItemModalFooter';
import MenuItemDetailsForm from 'catering/components/Forms/MenuItemDetailsForm';
import PageLoadSpinner from 'components/PageLoadSpinner';

import { useGoToCart } from 'catering/hooks/navigation';
import {
  useCart,
  useCateringLocationBySlug,
  useMenuItemDetailsByCartItemId,
  selectMenuItemFormInitialValues
} from 'catering/hooks/query';
import { buildCateringFormModifications } from 'helpers/forms/menuItem';
import { useUpdateCartItem } from 'catering/hooks/query/cart';
import useGetModifierGroups from 'hooks/useGetModifierGroups';
import { CART_ITEM } from 'constants/menu/formTypes';
import { PRODUCT_VARIANT_FIELD_NAME } from 'constants/menu/menuItemDetailForm';

function CartItemContainer({ setEditAlertContent }) {
  const { cartItemId, locationSlug } = useParams();
  const goToCart = useGoToCart();
  const updateCartItem = useUpdateCartItem();

  const {
    data: menuItemDetails,
    isLoading: isLoadingMenuItemDetails
  } = useMenuItemDetailsByCartItemId(cartItemId);

  const { data: cartData, isLoading: isCartLoading } = useCart({
    select: cart => ({
      ...cart,
      initialValues: selectMenuItemFormInitialValues(cart, cartItemId)
    })
  });

  const {
    data: cateringLocation,
    isLoading: isLoadingCateringLocation
  } = useCateringLocationBySlug(locationSlug);

  const modifierGroups = useGetModifierGroups(menuItemDetails);

  const handleSubmit = async formState => {
    const { id: productId } = menuItemDetails;
    let hasErrorUpdatingCart = false;

    const {
      quantity,
      'order-made-for': orderMadeFor,
      'special-instructions': specialInstructions,
      [PRODUCT_VARIANT_FIELD_NAME]: variant_id,
      ...modifications
    } = formState;

    try {
      const payload = {
        quantity,
        product_id: productId,
        order_made_for: orderMadeFor ?? '',
        special_instructions: specialInstructions ?? '',
        required_fields: {
          ...buildCateringFormModifications(modifierGroups, modifications)
        }
      };

      if (variant_id) {
        payload.variant_id = variant_id;
      }

      await updateCartItem.mutateAsync({
        cartItemId,
        data: payload
      });
    } catch (e) {
      hasErrorUpdatingCart = true;
    } finally {
      handleUpdateSnackBarMessage(hasErrorUpdatingCart);
      goToCart();
    }
  };

  const handleUpdateSnackBarMessage = hasErrorUpdatingCart => {
    const { name } = menuItemDetails;
    const editAlertContent = { isOpen: true, itemName: name };
    if (hasErrorUpdatingCart) {
      editAlertContent.errorState = true;
    }
    setEditAlertContent(editAlertContent);
  };

  if (isLoadingMenuItemDetails || isLoadingCateringLocation || isCartLoading) {
    return <PageLoadSpinner />;
  }

  if (!cartData) {
    goToCart();
    return <></>;
  }

  const { initialValues, ...cart } = cartData;

  return (
    <MenuItemDetailsModal
      menuItemDetails={menuItemDetails}
      menuItemDetailsForm={
        <MenuItemDetailsForm
          cart={cart}
          cateringLocation={cateringLocation}
          formType={CART_ITEM}
          FooterWrapperComponent={MenuItemModalFooter}
          initialValues={initialValues}
          menuItemDetails={menuItemDetails}
          onSubmit={handleSubmit}
          modifierGroups={modifierGroups}
        />
      }
    />
  );
}

CartItemContainer.propTypes = {
  setEditAlertContent: PropTypes.func.isRequired
};

export default CartItemContainer;
